import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TextField,
  Autocomplete,
  Paper,
  Box,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import type {
  MapProvider,
  AutocompleteResult
} from 'src/types/map-provider';

const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[4],
  marginTop: theme.spacing(0.5),
  maxHeight: 300,
  overflow: 'auto',
}));

const SearchContainer = styled(Box)({
  position: 'relative',
  width: '100%',
});

interface UniversalAutocompleteFieldProps {
  provider: MapProvider;
  value?: string;
  placeholder?: string;
  tooltipText?: string;
  onChange?: (value: string) => void;
  onPlaceSelect?: (result: AutocompleteResult | null) => void;
  disabled?: boolean;
  className?: string;
  debounceMs?: number;
  maxResults?: number;
}

export const UniversalAutocompleteField: React.FC<UniversalAutocompleteFieldProps> = ({
  provider,
  value = '',
  placeholder,
  tooltipText,
  onChange,
  onPlaceSelect,
  disabled = false,
  className,
  debounceMs = 300,
  maxResults = 10,
}) => {
  const { t } = useTranslation(['maps', 'common']);
  const [inputValue, setInputValue] = useState(value);
  const [options, setOptions] = useState<AutocompleteResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const debounceRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const abortControllerRef = useRef<AbortController | undefined>(undefined);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const fetchPredictions = useCallback(
    async (input: string, signal?: AbortSignal) => {
      if (!input.trim() || !provider || disabled) {
        setOptions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const results = await provider.getPlacePredictions(input);

        if (signal?.aborted) return;

        const limitedResults = results.slice(0, maxResults);
        setOptions(limitedResults);

        if (limitedResults.length === 0) {
          setError(t('search.noResults', { ns: 'maps' }));
        }
      } catch (err) {
        if (signal?.aborted) return;

        const errorMessage = (err as any)?.message || t('search.error', { ns: 'maps' });

        setError(errorMessage);
        setOptions([]);
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [provider, disabled, maxResults, t]
  );

  const handleInputChange = useCallback(
    (event: React.SyntheticEvent, newValue: string, reason: string) => {
      if (reason === 'input') {
        setInputValue(newValue);
        onChange?.(newValue);

        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }

        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        if (newValue.trim()) {
          const controller = new AbortController();
          abortControllerRef.current = controller;

          debounceRef.current = setTimeout(() => {
            fetchPredictions(newValue, controller.signal);
          }, debounceMs);
        } else {
          setOptions([]);
          setError(null);
          setLoading(false);
        }
      }
    },
    [onChange, fetchPredictions, debounceMs]
  );

  const handleOptionSelect = useCallback(
    async (event: React.SyntheticEvent, selectedValue: string | AutocompleteResult | null) => {
      if (typeof selectedValue === 'string') {
        return;
      }
      setOpen(false);

      if (!selectedValue || !provider) {
        onPlaceSelect?.(null);
        return;
      }

      setLoading(true);

      try {
        const placeDetails = await provider.getPlaceDetails(selectedValue.placeId);

        if (placeDetails) {
          setInputValue(placeDetails.formattedAddress || placeDetails.description);
          onChange?.(placeDetails.formattedAddress || placeDetails.description);
          onPlaceSelect?.(placeDetails);
        } else {
          setInputValue(selectedValue.formattedAddress || selectedValue.description);
          onChange?.(selectedValue.formattedAddress || selectedValue.description);
          onPlaceSelect?.(selectedValue);
        }
      } catch (err) {
        setInputValue(selectedValue.formattedAddress || selectedValue.description);
        onChange?.(selectedValue.formattedAddress || selectedValue.description);
        onPlaceSelect?.(selectedValue);
      } finally {
        setLoading(false);
      }
    },
    [provider, onChange, onPlaceSelect]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const autocompleteProps = {
    freeSolo: true,
    options,
    loading,
    value: inputValue,
    inputValue,
    open: open && (options.length > 0 || loading || !!error),
    onOpen: () => setOpen(true),
    onClose: () => setOpen(false),
    getOptionLabel: (option: AutocompleteResult | string) =>
      typeof option === 'string' ? option : option.description,
    isOptionEqualToValue: (option: AutocompleteResult, value: AutocompleteResult) =>
      option.placeId === value.placeId,
    onInputChange: handleInputChange,
    onChange: handleOptionSelect,
    disabled,
    className,
    filterOptions: (x: any) => x,
    PaperComponent: StyledPaper,
    ListboxProps: {
      style: { maxHeight: 250 },
    },
    renderInput: (params: any) => (
      <TextField
        {...params}
        placeholder={placeholder || t('search.placeholder', { ns: 'maps' })}
        variant="outlined"
        size="small"
        error={!!error}
        helperText={error}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'background.paper',
            borderRadius: 1,
            boxShadow: 2,
          },
        }}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {loading && <CircularProgress color="inherit" size={16} />}
              {params.InputProps.endAdornment}
            </>
          ),
        }}
      />
    ),
    renderOption: (props: any, option: AutocompleteResult) => (
      <Box
        component="li"
        {...props}
        sx={{
          padding: '8px 16px',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
          '&[aria-selected="true"]': {
            backgroundColor: 'action.selected',
          },
        }}
      >
        <Box>
          <Box
            sx={{
              fontWeight: 500,
              fontSize: '0.875rem',
              color: 'text.primary',
            }}
          >
            {option.description}
          </Box>
          {option.formattedAddress && option.formattedAddress !== option.description && (
            <Box
              sx={{
                fontSize: '0.75rem',
                color: 'text.secondary',
                mt: 0.5,
              }}
            >
              {option.formattedAddress}
            </Box>
          )}
        </Box>
      </Box>
    ),
  };

  const autocompleteElement = (
    <SearchContainer>
      <Autocomplete {...autocompleteProps} />
    </SearchContainer>
  );

  if (tooltipText) {
    return (
      <Tooltip title={tooltipText} arrow placement="top">
        <div>
          {autocompleteElement}
        </div>
      </Tooltip>
    );
  }

  return autocompleteElement;
};
